import { RebalanceProcessStatus } from "@core/enums/rebalance/rebalance-process-status-type";
import { CompositionRiskProfile } from "@core/models/composition-risk-profile.model";
import { Dividend } from "@core/models/dividend-model";
import { RebalanceModel } from "@core/models/rebalance.model";
import { RiskProfile } from "@core/models/risk-profile.model";
import { TaxRule } from "@core/models/tax-rule.model";
import { WithholdingConfig } from "@core/models/withholding-config.model";
import {
  dividendActions,
  rebalanceActions,
  settingsActions,
} from "@features/corporate-actions/store/corporate-actions.actions";
import { createReducer, on } from "@ngrx/store";

export const CORPORATE_ACTIONS_FEATURE_KEY = "corporateActions";

export interface CorporateActionsState {
  loading: boolean;
  withholdingLoading: boolean;
  lumpSumLoading: boolean;
  dividendList: Array<Dividend>;
  rebalanceData: Array<RebalanceModel>;
  riskProfiles: Array<RiskProfile | CompositionRiskProfile>;
  annualPaymentList: Array<TaxRule>;
  lumpSumList: Array<TaxRule>;
  withholdingConfigList: Array<WithholdingConfig>;
  withholdingConfig: WithholdingConfig | undefined;
  rebalance: Partial<RebalanceModel> | undefined;
}

export const initialState: CorporateActionsState = {
  loading: false,
  withholdingLoading: false,
  lumpSumLoading: false,
  dividendList: [],
  rebalanceData: [],
  riskProfiles: [],
  annualPaymentList: [],
  lumpSumList: [],
  withholdingConfigList: [],
  withholdingConfig: undefined,
  rebalance: undefined,
};

export const CORPORATE_ACTIONS_REDUCER = createReducer(
  initialState,
  on(
    dividendActions.SET_DIVIDENDS,
    (
      state: CorporateActionsState,
      action: {
        dividendList: Array<Dividend>;
      }
    ): CorporateActionsState => {
      return {
        ...state,
        dividendList: action.dividendList,
      };
    }
  ),

  on(
    rebalanceActions.LOAD_REBALANCE_DATA_SUCCESS,
    (
      state: CorporateActionsState,
      action: {
        rebalanceData: Array<RebalanceModel>;
      }
    ): CorporateActionsState => {
      return {
        ...state,
        rebalanceData: action.rebalanceData,
      };
    }
  ),
  on(
    rebalanceActions.DELETE_REBALANCE_SUCCESS,
    (
      state: CorporateActionsState,
      action: {
        rebalanceId: string;
      }
    ): CorporateActionsState => {
      // delete from state for optimistic rendering
      return {
        ...state,
        rebalanceData: state.rebalanceData.filter(
          (item) => item.id !== action.rebalanceId
        ),
      };
    }
  ),
  on(
    rebalanceActions.UPDATE_REBALANCE_EXECUTION_DATE_SUCCESS,
    (
      state: CorporateActionsState,
      action: {
        rebalanceId: string;
        executionDate: Date;
      }
    ): CorporateActionsState => {
      // update rebalance Status inside state for optimistic rendering
      return {
        ...state,
        rebalanceData: state.rebalanceData.map((item) =>
          item.id === action.rebalanceId
            ? {
                ...item,
                processStatus: RebalanceProcessStatus.PlannedExecution,
                executionDate: action.executionDate,
              }
            : item
        ),
      };
    }
  ),
  // loading reducer
  on(
    dividendActions.SET_DIVIDENDS_LOADING_STATUS,
    rebalanceActions.LOAD_REBALANCE_DATA_LOADING,
    rebalanceActions.DELETE_REBALANCE_LOADING,
    rebalanceActions.UPDATE_REBALANCE_EXECUTION_DATE_LOADING,
    settingsActions.SET_ANNUAL_PAYMENT_LOADING_STATUS,
    (
      state: CorporateActionsState,
      action: {
        loading: boolean;
      }
    ): CorporateActionsState => {
      return {
        ...state,
        loading: action.loading,
      };
    }
  ),
  on(
    settingsActions.SET_LUMP_SUM_PAYOUT_LOADING_STATUS,
    (
      state: CorporateActionsState,
      action: {
        loading: boolean;
      }
    ): CorporateActionsState => {
      return {
        ...state,
        lumpSumLoading: action.loading,
      };
    }
  ),
  on(
    settingsActions.SET_WITHHOLDING_LOADING_STATUS,
    (
      state: CorporateActionsState,
      action: {
        loading: boolean;
      }
    ): CorporateActionsState => {
      return {
        ...state,
        withholdingLoading: action.loading,
      };
    }
  ),
  on(
    rebalanceActions.SET_RISK_PROFILES,
    (
      state: CorporateActionsState,
      action: { riskProfiles: Array<RiskProfile | CompositionRiskProfile> }
    ): CorporateActionsState => {
      return {
        ...state,
        riskProfiles: action.riskProfiles,
      };
    }
  ),
  on(
    settingsActions.SET_ANNUAL_PAYMENT_LIST,
    (
      state: CorporateActionsState,
      action: { taxRuleList: Array<TaxRule> }
    ): CorporateActionsState => {
      return {
        ...state,
        annualPaymentList: action.taxRuleList,
      };
    }
  ),
  on(
    settingsActions.SET_LUMP_SUM_LIST,
    (
      state: CorporateActionsState,
      action: { taxRuleList: Array<TaxRule> }
    ): CorporateActionsState => {
      return {
        ...state,
        lumpSumList: action.taxRuleList,
      };
    }
  ),
  on(
    settingsActions.SET_WITHHOLDING_CONFIG_LIST,
    (
      state: CorporateActionsState,
      action: { withholdingConfigList: Array<WithholdingConfig> }
    ): CorporateActionsState => {
      return {
        ...state,
        withholdingConfigList: action.withholdingConfigList,
      };
    }
  ),
  on(
    settingsActions.SET_WITHHOLDING_CONFIG,
    (
      state: CorporateActionsState,
      action: { withholdingConfig: WithholdingConfig | undefined }
    ): CorporateActionsState => {
      return {
        ...state,
        withholdingConfig: action.withholdingConfig,
      };
    }
  )
);
